/**
 * ConfirmationModal.js
 * Created by calhosh on 6/23/2021.
 */

import React from 'react';
import styled from 'styled-components';
import {lightColor} from '@ey/styled-theme';
import {
	MotifModal,
	MotifModalHeader,
	MotifModalBody,
	MotifModalFooter,
	MotifButton,
	MotifIconButton
} from '@ey-xd/motif-react';
import {SVGICONS} from '@ey/icons';

function ConfirmationModal(props) {
	const {
		show,
		showButtons,
		title,
		description,
		descriptionText,
		bodyText,
		bodyTextValue,
		onHide,
		onConfirmClick,
		confirmBtnTitle,
		cancelBtnTitle,
		closeTitle,
		modalsize,
		modalbodystyle,
		modalContainerClass,
		showOnlyCloseButton
	} = props;

	return (
		<StyledMotifModal
			show={show}
			onClose={onHide}
			className={modalsize + ' ' + modalContainerClass}
		>
			<StyledModalHeader
				closeModalButton={
					<MotifIconButton
						type="button"
						onClick={onHide}
						className="motif-modal--header-icon-button"
						title={closeTitle}
					>
						<SVGICONS
							styleName="medium"
							name="close-outline"
							hoverText={closeTitle}
						/>
					</MotifIconButton>
				}
			>
				<section>{title}</section>
			</StyledModalHeader>
			<StyledModalBody className={modalbodystyle}>
				<section className="modalBodyWrapper customScrollbar">
					<section className="modalBodyDesc">{description}</section>
					{descriptionText && (
						<section className="modalBodyDescText">{descriptionText}</section>
					)}
					<section className="modalBodyTextWrapper">
						<section className="modalBodyText">{bodyText}&nbsp;</section>
						{bodyTextValue && (
							<section className="modalBodyTextValue">{bodyTextValue}</section>
						)}
					</section>
				</section>
			</StyledModalBody>
			{showButtons && (
				<StyledModalFooter>
					<MotifButton
						type="button"
						variant="primary-alt"
						onClick={onConfirmClick}
					>
						{confirmBtnTitle}
					</MotifButton>
					<MotifButton type="button" variant="secondary" onClick={onHide}>
						{cancelBtnTitle}
					</MotifButton>
				</StyledModalFooter>
			)}
			{showOnlyCloseButton && (
				<StyledModalFooter>
					<MotifButton type="button" variant="primary-alt" onClick={onHide}>
						{cancelBtnTitle}
					</MotifButton>
				</StyledModalFooter>
			)}
		</StyledMotifModal>
	);
}

export default ConfirmationModal;

const StyledMotifModal = styled(MotifModal)`
	.motif-modal-overlay {
		padding: 1rem;
		z-index: 1002;
	}
	&.motif-modal-content {
		width: 100%;
		max-width: 100%;
		height: 100%;
		max-height: 100%;

		&.small {
			width: 40% !important;
			max-width: 40% !important;
			height: auto !important;
			max-height: auto !important;
		}

		&.medium {
			width: 72% !important;
			max-width: 72% !important;
			height: calc(100vh - 4rem) !important;
			max-height: auto !important;
		}

		&.message {
			width: 30%;
			max-width: 30%;
			height: auto;
			max-height: auto;
		}
	}

	.motif-modal-body {
		padding: 0;
		position: relative;
		font-size: 1.2rem;
	}
`;

const StyledModalHeader = styled(MotifModalHeader)`
	&.motif-modal-header {
		background: ${lightColor[`grey500`]};
		color: ${lightColor[`white`]};
		padding: 1.2rem 2rem;
		height: auto;
		border: 0;
		.motif-modal-headline {
			color: ${lightColor[`white`]};
			font-weight: bold;
			font-size: 1.6rem;
			padding: 0;
		}
		.motif-modal--header-icon-button {
			color: ${lightColor[`white`]};
			box-shadow: none;
			width: 2.4rem;
			height: 2.4rem;
			margin-top: -0.3rem;
			&:hover,
			&:focus,
			&:active {
				background-color: transparent;
				svg {
					background-color: transparent;
					box-shadow: none;
					path {
						fill: ${lightColor[`white`]};
					}
				}
			}
			svg {
				width: 2.4rem;
				height: 2.4rem;
			}
		}
	}
`;
const StyledModalBody = styled(MotifModalBody)`
	&.motif-modal-body {
		overflow: hidden;
		height: inherit;
		&.modalMinHeight {
			min-height: 20rem;
		}
		.modalBodyWrapper {
			min-height: 15rem;
			max-height: 36rem;
			padding: 2rem 2rem 0 2rem;
			.modalBodyDesc {
				display: flex;
				font-size: 1.4rem;
				color: ${(props) => props.theme.color[`grey500`]};
				line-height: normal;
				.confirmModalDesc {
					p {
						color: ${(props) => props.theme.color[`grey500`]};
						font-size: 1.4rem;
					}
				}
				.guidanceWrapper {
					border-top: 0.1rem solid ${(props) => props.theme.color[`grey300`]};
					display: flex;
					padding-top: 0.5rem;
					margin-top: 3rem;
				}
				.guidanceIcon {
					display: inline-flex;
					margin: 0.3rem 0.3rem 0;
					align-items: center;
					@media only screen and (max-width: 1280px) {
						margin-top: 0.8rem;
					}
					svg {
						max-width: 2rem;
						max-height: 2rem;
						width: 2rem !important;
						height: 2rem !important;
					}
				}
				.confirmModalText {
					display: inline-flex;
					align-items: center;
					.motif-link {
						&:hover {
							border: 0;
						}
					}
					p {
						display: inline-block;
						margin: 0 0.3rem;
						font-size: 1rem;
					}
					&: last-child {
						p {
							display: none;
						}
					}
				}
			}
			.modalBodyDescText {
				display: inline-flex;
				font-weight: bold;
				font-size: 1.4rem;
				margin-top: 0.5rem;
				color: ${(props) => props.theme.color[`grey500`]};
			}
			.modalBodyTextWrapper {
				display: flex;
				padding: 2rem 0;
				width: 100%;
			}
			.modalBodyText {
				display: inline-flex;
				font-weight: 300;
				font-size: 1.4rem;
				color: ${(props) => props.theme.color[`grey500`]};
			}
			.modalBodyTextValue {
				display: inline-flex;
				font-weight: bold;
				font-size: 1.6rem;
				color: ${(props) => props.theme.color[`grey500`]};
			}
		}
	}
`;
const StyledModalFooter = styled(MotifModalFooter)`
	&.motif-modal-footer {
		padding: 1rem 2rem;
		button {
			font-size: 1.4rem;
			padding: 0.7rem;
			min-width: 8.8rem;
			&:focus {
				box-shadow: none;
			}
		}
		.motif-button-primary-alt {
			background: ${(props) => props.theme.color[`blackyellow`]};
		}
	}
`;
